import { skipToken } from '@reduxjs/toolkit/query/react'
import { TeamDriverRow, TeamMemberRow } from '@numeo/types-main-web-app'
import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/hooks'
import type { RootState } from '@/store'
import { teamApi, useListTeamDriversQuery, useListTeamMembersQuery } from '@/store/api/teamApi'
import { listDriversFromUsers, listMembersFromUsers, TEAM_MAX_IDS } from '@/utils/team/teamQuery'
import { selectTeamApplicationId, useTeamDirectoryEnabled } from './useTeamDirectoryEnabled'

interface RowsByIdState<T> {
    /** The rows found, by id. An id the caller may not see (or nobody has) is absent. */
    rows: ReadonlyMap<string, T>
    /** Some of the ids are still being asked for. */
    isLoading: boolean
    /** A request for some of the ids failed; the rows hold what the others answered. */
    isError: boolean
}

type ChunkState<T> = { data?: { items: T[] }; isError: boolean }
const chunkStateOf = <T>(result: ChunkState<T>): ChunkState<T> => ({ data: result.data, isError: result.isError })
const sameChunks = <T>(a: ChunkState<T>[], b: ChunkState<T>[]): boolean =>
    a.length === b.length && a.every((chunk, index) => chunk.data === b[index].data && chunk.isError === b[index].isError)

const rowsOf = <T extends { id: string }>(chunks: ChunkState<T>[]): RowsByIdState<T> => {
    const rows = new Map<string, T>()
    for (const chunk of chunks) for (const row of chunk.data?.items ?? []) rows.set(row.id, row)
    // A failed chunk is settled, not loading: a screen waiting on these rows must not wait forever.
    return { rows, isLoading: chunks.some((chunk) => !chunk.data && !chunk.isError), isError: chunks.some((chunk) => chunk.isError) }
}

const chunksOf = (key: string): string[][] => {
    const ids = key ? key.split(',') : []
    const chunks: string[][] = []
    for (let start = 0; start < ids.length; start += TEAM_MAX_IDS) chunks.push(ids.slice(start, start + TEAM_MAX_IDS))
    return chunks
}

const keyOf = (ids: readonly (string | null | undefined)[]): string => [...new Set(ids.filter((id): id is string => !!id).map(String))].sort().join(',')

/**
 * Past this many ids a lookup is answered from the shared whole list (every driver, or every member) instead of `ids=`
 * reads: a screen naming many people (a fleet's trucks, every group) then costs the one read the pickers already made, and
 * a changing set of ids (rows scrolling into view) asks for nothing new. A few ids keep their small `ids=` read.
 */
const SHARED_LOOKUP_FROM = 20

/** The rows among `items` whose id is in `key`, as a lookup's answer. */
const pick = <T extends { id: string }>(items: readonly T[], key: string): Map<string, T> => {
    const wanted = new Set(key ? key.split(',') : [])
    return new Map(items.filter((row) => wanted.has(row.id)).map((row) => [row.id, row]))
}

const countOf = (key: string): number => (key ? key.split(',').length : 0)

/**
 * The directory rows of exactly these drivers, however many (the `ids=` list query, 200 ids per request, cached five
 * minutes like any list): for a screen that shows a field of the drivers its own records point at (a truck's drivers'
 * Telegram link, pay type, status). Before the directory serves the company, the same rows from `application.users`.
 */
export const useTeamDriverRowsByIds = (ids: readonly (string | null | undefined)[], options: { skip?: boolean } = {}): RowsByIdState<TeamDriverRow> => {
    const dispatch = useAppDispatch()
    const enabled = useTeamDirectoryEnabled()
    const applicationId = useAppSelector(selectTeamApplicationId)
    // Skipped: no ids asked, so nothing is read (a closed modal reads nothing).
    const key = options.skip ? '' : keyOf(ids)
    const shared = countOf(key) > SHARED_LOOKUP_FROM
    const everyone = useListTeamDriversQuery(enabled && applicationId && shared ? { applicationId, all: true } : skipToken)
    const args = useMemo(
        () => (enabled && applicationId && !shared ? chunksOf(key).map((chunk) => ({ applicationId, ids: chunk })) : []),
        [enabled, applicationId, key, shared]
    )

    useEffect(() => {
        const subscriptions = args.map((arg) => dispatch(teamApi.endpoints.listTeamDrivers.initiate(arg)))
        return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
    }, [dispatch, args])

    const selectors = useMemo(() => args.map((arg) => teamApi.endpoints.listTeamDrivers.select(arg)), [args])
    const pages = useAppSelector((state: RootState) => selectors.map((select) => chunkStateOf(select(state))), sameChunks)
    const users = useAppSelector((state) => (enabled ? undefined : state.auth.application?.users))

    return useMemo((): RowsByIdState<TeamDriverRow> => {
        if (!enabled) {
            const wanted = key ? key.split(',') : []
            const page = wanted.length ? listDriversFromUsers(users, { ids: wanted }) : undefined
            return { rows: new Map((page?.items ?? []).map((row) => [row.id, row])), isLoading: false, isError: false }
        }
        if (shared) return { rows: pick(everyone.data?.items ?? [], key), isLoading: !everyone.data && !everyone.isError, isError: everyone.isError }
        return rowsOf(pages)
    }, [enabled, key, users, pages, shared, everyone.data, everyone.isError])
}

/** The same for members: the rows of exactly these members (an assignee's mailboxes, a dispatcher's Telegram group). */
export const useTeamMemberRowsByIds = (ids: readonly (string | null | undefined)[]): RowsByIdState<TeamMemberRow> => {
    const dispatch = useAppDispatch()
    const enabled = useTeamDirectoryEnabled()
    const applicationId = useAppSelector(selectTeamApplicationId)
    const key = keyOf(ids)
    // Any ids: the shared read of every member, while it holds them all (a company of at most 200 members). A company's
    // members are few, so even one id is answered from the read every member list on the page already shares, and rows
    // scrolling into view ask for nothing new.
    const many = countOf(key) > 0
    const everyone = useListTeamMembersQuery(enabled && applicationId && many ? { applicationId, page: 1, limit: TEAM_MAX_IDS } : skipToken)
    const everyoneCovers = !!everyone.data && everyone.data.pagination.total <= everyone.data.items.length
    const shared = many && (everyoneCovers || (!everyone.data && !everyone.isError))
    const args = useMemo(
        () => (enabled && applicationId && !shared ? chunksOf(key).map((chunk) => ({ applicationId, ids: chunk })) : []),
        [enabled, applicationId, key, shared]
    )

    useEffect(() => {
        const subscriptions = args.map((arg) => dispatch(teamApi.endpoints.listTeamMembers.initiate(arg)))
        return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
    }, [dispatch, args])

    const selectors = useMemo(() => args.map((arg) => teamApi.endpoints.listTeamMembers.select(arg)), [args])
    const pages = useAppSelector((state: RootState) => selectors.map((select) => chunkStateOf(select(state))), sameChunks)
    const users = useAppSelector((state) => (enabled ? undefined : state.auth.application?.users))
    const ownerId = useAppSelector((state) => (state.auth.application?.owner ? String(state.auth.application.owner) : undefined))

    return useMemo((): RowsByIdState<TeamMemberRow> => {
        if (!enabled) {
            const wanted = key ? key.split(',') : []
            const page = wanted.length ? listMembersFromUsers(users, ownerId, { ids: wanted }) : undefined
            return { rows: new Map((page?.items ?? []).map((row) => [row.id, row])), isLoading: false, isError: false }
        }
        if (shared) return { rows: pick(everyone.data?.items ?? [], key), isLoading: !everyone.data, isError: false }
        return rowsOf(pages)
    }, [enabled, key, users, ownerId, pages, shared, everyone.data])
}
